import { Shield, Mail, Phone, MapPin, Globe } from 'lucide-react';
import { BRAND } from '@/data/content';

const SectionTitle = ({ children }) => (
  <h2
    style={{
      color: 'var(--gold)',
      fontSize: '22px',
      fontWeight: 600,
      marginTop: '40px',
      marginBottom: '14px',
      letterSpacing: '0.01em',
    }}
  >
    {children}
  </h2>
);

const SubHeading = ({ children }) => (
  <h3
    style={{
      color: 'var(--text-primary, #f4f4f4)',
      fontSize: '16px',
      fontWeight: 600,
      marginTop: '20px',
      marginBottom: '8px',
      letterSpacing: '0.02em',
    }}
  >
    {children}
  </h3>
);

const Paragraph = ({ children }) => (
  <p
    style={{
      color: 'var(--text-secondary, #c9c9c9)',
      fontSize: '15px',
      lineHeight: 1.75,
      marginBottom: '12px',
    }}
  >
    {children}
  </p>
);

const List = ({ items }) => (
  <ul
    style={{
      color: 'var(--text-secondary, #c9c9c9)',
      fontSize: '15px',
      lineHeight: 1.75,
      paddingLeft: '20px',
      marginBottom: '12px',
    }}
  >
    {items.map((item, i) => (
      <li key={i} style={{ marginBottom: '6px' }}>{item}</li>
    ))}
  </ul>
);

const PrivacyPolicyPage = () => {
  return (
    <div style={{ paddingTop: '80px' }} data-testid="privacy-policy-page">
      <section>
        <div className="container" style={{ maxWidth: '900px' }}>

          {/* Header */}
          <div className="section-header" style={{ textAlign: 'left', marginBottom: '32px' }}>
            <div className="section-eyebrow" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Shield size={14} /> Legal
            </div>
            <h1 className="section-title" style={{ fontSize: '38px', lineHeight: 1.2 }}>
              Privacy <span className="gold-gradient">Policy</span>
            </h1>
            <p className="section-subtitle" style={{ maxWidth: '760px', margin: '12px 0 0' }}>
              This Privacy Policy explains how EuroElite Education Consultancy collects, uses,
              stores, and protects your personal information when you use our website, mobile
              application, and related services.
            </p>
            <div
              style={{
                marginTop: '18px',
                fontSize: '13px',
                color: 'var(--gold)',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
              data-testid="privacy-effective-date"
            >
              Effective Date: August 11, 2026 &nbsp;|&nbsp; Last Updated: August 11, 2026
            </div>
          </div>

          {/* Introduction */}
          <SectionTitle>1. Introduction</SectionTitle>
          <Paragraph>
            EuroElite Education Consultancy (“<strong>EuroElite</strong>”, “<strong>we</strong>”,
            “<strong>our</strong>”, or “<strong>us</strong>”) is committed to protecting your
            privacy and safeguarding your personal information. This Privacy Policy applies to
            information collected through our website{' '}
            <a href="https://www.euroelite.in" style={{ color: 'var(--gold)' }}>www.euroelite.in</a>,
            the <strong>EuroElite Connect</strong> mobile application, our Career Portal,
            counselling forms, WhatsApp inquiries, phone communications, and any other services we
            provide (collectively, the “<strong>Services</strong>”).
          </Paragraph>
          <Paragraph>
            By accessing or using our Services, you consent to the collection and use of your
            information as described in this Policy. If you do not agree with any part of this
            Policy, please do not use our Services.
          </Paragraph>

          {/* Information We Collect */}
          <SectionTitle>2. Information We Collect</SectionTitle>

          <SubHeading>2.1 Information You Provide Directly</SubHeading>
          <List items={[
            'Contact details such as full name, email address, phone number, and city.',
            'Academic information including degree level, courses of interest, and destination country.',
            'Career Portal data such as resumes, work history, education, skills, and career preferences.',
            'Messages, questions, and feedback you send us via forms, email, or WhatsApp.',
            'Payment-related identifiers when you purchase premium services (processed securely via Razorpay).',
          ]} />

          <SubHeading>2.2 Information Collected Automatically</SubHeading>
          <List items={[
            'Device information (device type, operating system, browser type and version).',
            'Log data (IP address, access times, pages viewed, referring URLs).',
            'Usage analytics to help us improve the Services.',
            'Cookies and similar tracking technologies (see Section 6).',
          ]} />

          <SubHeading>2.3 Information from Third Parties</SubHeading>
          <List items={[
            'Payment status and transaction identifiers from Razorpay.',
            'Delivery status of transactional emails from our email service provider.',
            'Publicly available information from partner universities and scholarship bodies.',
          ]} />

          {/* How We Use */}
          <SectionTitle>3. How We Use Your Information</SectionTitle>
          <Paragraph>We use the information we collect for the following purposes:</Paragraph>
          <List items={[
            'To provide counselling, admissions guidance, and study-abroad consultation services.',
            'To generate AI-powered career analyses and premium career intelligence reports.',
            'To process payments and deliver purchased premium services.',
            'To respond to your inquiries via email, phone, or WhatsApp.',
            'To send transactional communications (application updates, payment receipts, PDF reports).',
            'To improve, personalize, and secure our Services.',
            'To comply with applicable laws and enforce our Terms of Service.',
          ]} />

          {/* EuroElite Connect App */}
          <SectionTitle>4. EuroElite Connect Mobile Application</SectionTitle>
          <Paragraph>
            The EuroElite Connect Android application provides users with study-abroad guidance,
            career tools, and access to premium services. In addition to the information described
            above, the app may request standard mobile permissions strictly necessary for its
            functionality — for example, internet access, notifications, and optional storage
            access to attach or upload resume files.
          </Paragraph>
          <Paragraph>
            All data transmitted between the mobile app and our servers is encrypted using
            industry-standard HTTPS. We do not access your contacts, location, camera, microphone,
            SMS, or call logs. You may revoke any granted permission at any time from your device
            settings.
          </Paragraph>

          {/* Third-Party Services */}
          <SectionTitle>5. Third-Party Services</SectionTitle>
          <Paragraph>
            We work with trusted third-party providers to deliver our Services. These providers
            operate under their own privacy policies and process only the data required for their
            specific function:
          </Paragraph>
          <List items={[
            'Razorpay – processes payments for premium services. Card, UPI, and banking details are entered directly on Razorpay’s secure environment and are never stored on our servers.',
            'WhatsApp (Meta) – used for optional customer communication when you initiate a chat with us.',
            'Email delivery service – used to send transactional emails such as receipts and premium report notifications.',
            'AI service providers – used to generate career analysis and premium career reports. Only the resume text and career preferences necessary for analysis are shared, and inputs are not used to train third-party models.',
            'Google Maps – embedded on our contact page for office location display.',
          ]} />

          {/* Cookies */}
          <SectionTitle>6. Cookies &amp; Similar Technologies</SectionTitle>
          <Paragraph>
            We use cookies and similar technologies to remember your preferences, maintain your
            session on the Career Portal, understand how our Services are used, and improve
            performance. You may disable cookies through your browser settings; however, doing so
            may affect certain features (such as staying logged in to the Career Portal).
          </Paragraph>

          {/* Data Retention & Security */}
          <SectionTitle>7. Data Retention &amp; Security</SectionTitle>
          <Paragraph>
            We retain your personal information only for as long as necessary to fulfil the
            purposes described in this Policy, comply with legal obligations, resolve disputes, and
            enforce our agreements. Career Portal resumes and analyses are retained until you
            request deletion or close your account.
          </Paragraph>
          <Paragraph>
            We implement reasonable administrative, technical, and physical safeguards designed to
            protect your information — including HTTPS encryption in transit, restricted access
            controls, secure server infrastructure, and secure storage of payment identifiers.
            However, no method of transmission or storage is 100% secure, and we cannot guarantee
            absolute security.
          </Paragraph>

          {/* Your Rights */}
          <SectionTitle>8. Your Rights Under India’s DPDP Act, 2023</SectionTitle>
          <Paragraph>
            As a Data Principal under the Digital Personal Data Protection Act, 2023, you have the
            following rights with respect to your personal data:
          </Paragraph>
          <List items={[
            'Right to access – request a summary of the personal data we hold about you.',
            'Right to correction and erasure – request correction of inaccurate data or deletion of your data.',
            'Right to grievance redressal – raise any concerns regarding processing of your personal data.',
            'Right to nominate – appoint a nominee to exercise your rights in the event of death or incapacity.',
            'Right to withdraw consent – withdraw previously given consent at any time.',
          ]} />
          <Paragraph>
            To exercise any of these rights, please contact us using the details provided in
            Section 11. We will respond within a reasonable timeframe as required by applicable
            law.
          </Paragraph>

          {/* Account Deletion */}
          <SectionTitle>9. Account &amp; Data Deletion</SectionTitle>
          <Paragraph>
            You may request deletion of your Career Portal account, uploaded resumes, generated
            analyses, and associated personal data at any time. To submit an account or data
            deletion request:
          </Paragraph>
          <List items={[
            <>Send an email to <a href={`mailto:${BRAND.email}`} style={{ color: 'var(--gold)' }}>{BRAND.email}</a> with the subject line “Account Deletion Request”.</>,
            'Include the registered email address associated with your account.',
            'We will verify your identity and process your request within 30 days.',
          ]} />
          <Paragraph>
            Please note that certain information may be retained where required by law (e.g.,
            payment records for taxation and audit purposes) or where necessary to protect our
            legitimate business interests.
          </Paragraph>

          {/* Children's Privacy */}
          <SectionTitle>10. Children’s Privacy</SectionTitle>
          <Paragraph>
            Our Services are intended primarily for individuals aged 16 and above who are seeking
            higher education opportunities. We do not knowingly collect personal information from
            children under 16 without verifiable parental or guardian consent. If you believe a
            child has provided us with personal data without such consent, please contact us and
            we will promptly delete the information.
          </Paragraph>

          {/* Policy Updates */}
          <SectionTitle>11. Changes to This Policy</SectionTitle>
          <Paragraph>
            We may update this Privacy Policy from time to time to reflect changes in our
            practices, technology, legal requirements, or other factors. When we make material
            changes, we will update the “Last Updated” date at the top of this page and, where
            appropriate, notify you through our Services. Your continued use of the Services after
            such changes constitutes acceptance of the updated Policy.
          </Paragraph>

          {/* Contact */}
          <SectionTitle>12. Contact Us</SectionTitle>
          <Paragraph>
            If you have any questions, concerns, complaints, or requests relating to this Privacy
            Policy or the handling of your personal data, please contact us using the details
            below. We are committed to resolving your concerns promptly and transparently.
          </Paragraph>

          <div
            style={{
              marginTop: '18px',
              padding: '22px 24px',
              background: 'rgba(212, 168, 73, 0.06)',
              border: '1px solid var(--border-subtle, rgba(255,255,255,0.08))',
              borderRadius: '6px',
            }}
            data-testid="privacy-contact-block"
          >
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '14px' }}>
              <MapPin size={18} style={{ color: 'var(--gold)', marginTop: '3px', flexShrink: 0 }} />
              <div style={{ color: 'var(--text-secondary, #c9c9c9)', fontSize: '15px', lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--text-primary, #f4f4f4)' }}>EuroElite Education Consultancy</strong><br />
                {BRAND.address}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '14px' }}>
              <Mail size={18} style={{ color: 'var(--gold)', marginTop: '3px', flexShrink: 0 }} />
              <div style={{ color: 'var(--text-secondary, #c9c9c9)', fontSize: '15px' }}>
                <a href={`mailto:${BRAND.email}`} style={{ color: 'var(--gold)' }}>{BRAND.email}</a>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '14px' }}>
              <Phone size={18} style={{ color: 'var(--gold)', marginTop: '3px', flexShrink: 0 }} />
              <div style={{ color: 'var(--text-secondary, #c9c9c9)', fontSize: '15px', lineHeight: 1.7 }}>
                {BRAND.phones.map((p, i) => (
                  <div key={i}><a href={`tel:${p.replace(/\s/g, '')}`} style={{ color: 'var(--text-secondary, #c9c9c9)' }}>{p}</a></div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <Globe size={18} style={{ color: 'var(--gold)', marginTop: '3px', flexShrink: 0 }} />
              <div style={{ color: 'var(--text-secondary, #c9c9c9)', fontSize: '15px' }}>
                <a href="https://www.euroelite.in" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)' }}>
                  {BRAND.website}
                </a>
              </div>
            </div>
          </div>

          <div style={{ height: '60px' }} />
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
